import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useVisualViewportBox } from '../../hooks/useVisualViewport';
import { DECRYPT_ERROR_TEXT } from '../../services/chatCrypto.mjs';
import { shortFingerprint } from '../../services/chatTrust.mjs';
import { displayName, nameInitials } from '../../services/displayName';
import { colors, radii, FONT } from '../../theme';
import { ArrowLeft, Phone, MessageSquare, Send, Mic, Paperclip, Camera, Image, FileText, X, Square, Edit3, Check, ChevronDown, CornerUpLeft, Upload, ShieldAlert } from 'lucide-react';
import { api } from '../../services/api';
import { compressImage, COMPRESS_DEFAULT, COMPRESS_HD } from '../../services/imageCompress';
import { imageCache } from '../../services/imageCache';
import VoiceMessage from './VoiceMessage';
import ImageMessage from './ImageMessage';
import FileMessageComp from './FileMessage';
import MediaViewer from './MediaViewer';
import ImageEditor from './ImageEditor';

// ─── ReadReceipt (double-check / single-check SVG) ──────────
function ReadReceipt({ read }) {
  if (read) {
    return (
      <svg width="16" height="11" viewBox="0 0 16 11" fill="none" style={{ marginLeft: 4, flexShrink: 0 }}>
        <path d="M1 5.5L4.5 9L11 2" stroke={colors.greenDot} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 5.5L8.5 9L15 2" stroke={colors.greenDot} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return (
    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" style={{ marginLeft: 4, flexShrink: 0 }}>
      <path d="M1 5.5L4.5 9L11 2" stroke={colors.gray400} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── Reply quote block (shown inside bubble) ────────────────
function ReplyQuote({ msg, onTap, chatUserName }) {
  if (!msg.reply_to_id) return null;
  const isReplyFromMe = msg.reply_from_id === msg.from_user_id;
  const replyName = msg.reply_from_name || (isReplyFromMe ? 'Вы' : chatUserName || '?');
  const replyType = msg.reply_msg_type || 'text';
  const previewText = replyType === 'voice' ? '🎤 Голосовое'
    : replyType === 'image' ? '📷 Фото'
    : replyType === 'file' ? '📎 Файл'
    : (msg.reply_content || '').slice(0, 60) || '...';

  return (
    <div onClick={(e) => { e.stopPropagation(); onTap?.(msg.reply_to_id); }} style={CS.replyQuote}>
      <div style={CS.replyQuoteBar} />
      <div style={{ minWidth: 0 }}>
        <div style={CS.replyQuoteName}>{replyName}</div>
        <div style={CS.replyQuoteText}>{previewText}</div>
      </div>
    </div>
  );
}

// ─── Message content renderer ───────────────────────────────
function MessageContent({ msg, isMine, onOpenMedia }) {
  const msgType = msg.type === 'chat_message' ? (msg.msgType || 'text') : (msg.msgType || msg.type || 'text');

  // A message we could not open, or could not send, is shown as such. A
  // conversation with a silent hole in it is worse than one that says a
  // message did not arrive — and after E2EE the owner can no longer look in
  // the database to find out which.
  //
  // A row still marked as ciphertext has not been through the intake pipeline,
  // whatever put it here. `enc` is what the envelope says before it is opened;
  // opening rewrites it to what the message actually is. So this is the
  // invariant, and it belongs in the one place that draws a bubble: a row that
  // still calls itself encrypted is never drawn as text. Without it, a single
  // mishandled echo printed a screenful of base64 where somebody's own message
  // should have been.
  const stillSealed = !msg.decryptError && (msg.msgType === 'enc' || msg.type === 'enc');
  if (msg.decryptError || msg.sendError || stillSealed) {
    const why = msg.sendError === 'E_NO_SESSION' ? 'Шифрование ещё не готово — сообщение не отправлено'
      : msg.sendError === 'E_UNTRUSTED' ? 'Не отправлено: у собеседника новое устройство, подтвердите его выше'
      : msg.sendError === 'E_RETRY' ? 'Пока не отправлено — повторю сам' + (msg.sendErrorDetail ? ` (${msg.sendErrorDetail})` : '')
      : msg.sendError ? 'Не удалось зашифровать — сообщение не отправлено'
      : stillSealed ? 'Сообщение ещё не открыто'
      : DECRYPT_ERROR_TEXT[msg.decryptError] || 'Сообщение не расшифровано';
    return (
      <div style={{ ...CS.bubbleText, display: 'flex', alignItems: 'center', gap: 6, opacity: 0.75, fontStyle: 'italic' }}>
        <ShieldAlert size={14} color={colors.amber} />
        {why}
      </div>
    );
  }

  if (msgType === 'voice' && msg.file_id) {
    return <VoiceMessage fileId={msg.file_id} fileKey={msg.file_key} duration={msg.file_duration} isMine={isMine} />;
  }
  if (msgType === 'image' && msg.file_id) {
    return <ImageMessage fileId={msg.file_id} fileKey={msg.file_key} fileMime={msg.file_mime} fileName={msg.file_name} isMine={isMine}
      onOpen={(fid, fname) => onOpenMedia({ fileId: fid, fileName: fname, mimeType: msg.file_mime || 'image/*', type: 'image', fileKey: msg.file_key })} />;
  }
  if (msgType === 'file' && msg.file_id) {
    return <FileMessageComp fileId={msg.file_id} fileKey={msg.file_key} fileMime={msg.file_mime} fileName={msg.file_name} fileSize={msg.file_size} isMine={isMine}
      onPreview={(fid, fname, mime) => onOpenMedia({ fileId: fid, fileName: fname, mimeType: mime, type: 'file', fileKey: msg.file_key })} />;
  }
  return <div style={CS.bubbleText}>{msg.content}</div>;
}

// ─── Swipeable message row ──────────────────────────────────
function SwipeableMessage({ children, onReply, onLongPress, direction }) {
  const rowRef = useRef(null);
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const triggeredRef = useRef(false);
  const longPressRef = useRef(null);
  const movedRef = useRef(false);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startXRef.current = t.clientX;
    startYRef.current = t.clientY;
    triggeredRef.current = false;
    movedRef.current = false;
    // Long-press timer (500ms)
    longPressRef.current = setTimeout(() => {
      if (!movedRef.current && onLongPress) {
        if (navigator.vibrate) navigator.vibrate(15);
        onLongPress(t.clientX, t.clientY);
      }
    }, 500);
  };
  const onTouchMove = (e) => {
    if (startXRef.current === null) return;
    const dx = e.touches[0].clientX - startXRef.current;
    const dy = Math.abs(e.touches[0].clientY - startYRef.current);
    // Cancel long-press if finger moved
    if (Math.abs(dx) > 8 || dy > 8) {
      movedRef.current = true;
      if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
    }
    // Only allow swipe right (for reply)
    if (dx > 0 && dx < 80) {
      setOffset(dx);
    } else if (dx >= 80 && !triggeredRef.current) {
      triggeredRef.current = true;
      if (navigator.vibrate) navigator.vibrate(10);
    }
  };
  const onTouchEnd = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
    if (triggeredRef.current) {
      onReply();
    }
    setOffset(0);
    startXRef.current = null;
  };

  const showArrow = offset > 20 || triggeredRef.current;

  return (
    <div
      ref={rowRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onContextMenu={(e) => e.preventDefault()}
      style={{ position: 'relative', overflowX: 'clip', overflowY: 'visible' }}
    >
      {/* Reply arrow indicator */}
      {showArrow && (
        <div style={{
          position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
          opacity: Math.min(offset / 60, 1),
          transition: offset === 0 ? 'opacity 0.2s' : 'none',
        }}>
          <CornerUpLeft size={20} color={colors.avatarBlue} />
        </div>
      )}
      <div style={{
        transform: `translateX(${offset}px)`,
        transition: offset === 0 ? 'transform 0.2s ease-out' : 'none',
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── Anti-spam constants ────────────────────────────────────
const SPAM_WINDOW_MS = 10000;
const SPAM_MSG_LIMIT = 2;
const COMPOSE_TIMEOUT_S = 5 * 60;

// ─── Styles reference (used before definition via hoisting) ──
const CS = {};

// ─── Full-screen Chat Overlay Component ─────────────────────
export default function ChatOverlay({ untrustedDevices = [], onTrustDevice, onAcceptNewAccountKey, chatUser, chatMessages, chatInput, setChatInput, sendMessage, onBack, onCall, userId, userName, messagesEndRef, fmtTime, formatLastSeen, spamStateRef, onLoadOlder, hasMoreHistory, anchorMessageId }) {
  const overlayRef = useRef(null);
  const messagesRef = useRef(null);
  const textareaRef = useRef(null);
  // The overlay follows the visible part of the page while the keyboard is
  // up (useVisualViewport.js): header at the visible top, composer just above
  // the keys — instead of moving the composer alone while the rest scrolled away.
  const vvBox = useVisualViewportBox();

  // ── Reply state ──
  const [replyTo, setReplyTo] = useState(null); // the message being replied to

  // ── Context menu (long-press) ──
  const [ctxMenu, setCtxMenu] = useState(null); // { msg, x, y }

  // ── Attachment state ──
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // ── Image upload state ──
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [hdMode, setHdMode] = useState(false);
  // file input refs removed — using <label htmlFor> instead

  // ── Document viewer ──
  const [mediaViewer, setMediaViewer] = useState(null); // { fileId, fileName, mimeType, type }
  const [imageEditor, setImageEditor] = useState(null); // { fileId, blobUrl }

  // ── iOS native editor callback ──
  useEffect(() => {
    if (!window.VoxioxBridge) window.VoxioxBridge = {};
    window.VoxioxBridge.onEditedImageReady = (fileId) => {
      sendMessage(null, 'image', fileId);
      setMediaViewer(null);
    };
    return () => { if (window.VoxioxBridge) delete window.VoxioxBridge.onEditedImageReady; };
  }, [sendMessage]);

  // ── Voice recording state ──
  const [recording, setRecording] = useState(false);
  const [recordLocked, setRecordLocked] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const [slideX, setSlideX] = useState(0);         // horizontal drag offset (negative = left)
  const [lockProgress, setLockProgress] = useState(0); // 0..1 how close to lock (finger up)
  const [waveformBars, setWaveformBars] = useState([]); // array of 0..1 values for visualization
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordStartRef = useRef(null);
  const recordTimerRef = useRef(null);
  const touchStartRef = useRef(null);
  const analyserRef = useRef(null);
  const waveIntervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const recordingRef = useRef(false); // sync ref for callbacks
  const recordLockedRef = useRef(false); // sync ref for mouse handler closure

  // ── Keyboard state ──
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // ── Anti-spam compose mode (restored from spamStateRef on mount) ──
  const savedSpam = spamStateRef?.current?.[chatUser?.id];
  const [composeMode, setComposeMode] = useState(savedSpam?.composeMode || false);
  const [bufferedMessages, setBufferedMessages] = useState(savedSpam?.bufferedMessages || []);
  const [composeCountdown, setComposeCountdown] = useState(COMPOSE_TIMEOUT_S);
  const [editingBuffer, setEditingBuffer] = useState(false);
  const [editBufferText, setEditBufferText] = useState('');
  const sentTimesRef = useRef(savedSpam?.sentTimes || []);
  const composeIntervalRef = useRef(null);
  const composeDeadlineRef = useRef(null);
  const editingRef = useRef(false);

  useEffect(() => { editingRef.current = editingBuffer; }, [editingBuffer]);

  // Restore countdown from saved deadline
  useEffect(() => {
    if (savedSpam?.deadline && savedSpam.composeMode) {
      const remaining = Math.max(0, Math.ceil((savedSpam.deadline - Date.now()) / 1000));
      if (remaining > 0) {
        composeDeadlineRef.current = savedSpam.deadline;
        setComposeCountdown(remaining);
        // Restart the interval
        composeIntervalRef.current = setInterval(() => {
          const r = Math.max(0, Math.ceil((composeDeadlineRef.current - Date.now()) / 1000));
          setComposeCountdown(r);
          if (r <= 0) {
            clearInterval(composeIntervalRef.current);
            composeIntervalRef.current = null;
            if (!editingRef.current) {
              setBufferedMessages(prev => {
                if (prev.length > 0) sendMessage(prev.join('\n'));
                return [];
              });
              setComposeMode(false);
              setEditingBuffer(false);
            }
          }
        }, 500);
      } else {
        // Timer expired while away — send buffered messages
        if (savedSpam.bufferedMessages?.length > 0) {
          sendMessage(savedSpam.bufferedMessages.join('\n'));
        }
        setComposeMode(false);
        setBufferedMessages([]);
      }
    }
  }, []); // eslint-disable-line

  // Save anti-spam state on unmount
  useEffect(() => {
    const uid = chatUser?.id;
    return () => {
      if (!spamStateRef?.current || !uid) return;
      spamStateRef.current[uid] = {
        composeMode: composeModeRef.current,
        bufferedMessages: bufferedMsgsRef.current,
        deadline: composeDeadlineRef.current,
        sentTimes: sentTimesRef.current,
      };
    };
  }, [chatUser?.id, spamStateRef]);

  // Keep refs in sync for unmount save
  const composeModeRef = useRef(composeMode);
  const bufferedMsgsRef = useRef(bufferedMessages);
  useEffect(() => { composeModeRef.current = composeMode; }, [composeMode]);
  useEffect(() => { bufferedMsgsRef.current = bufferedMessages; }, [bufferedMessages]);

  useEffect(() => { setKeyboardVisible(vvBox.keyboard > 100); }, [vvBox.keyboard]);

  // ── Scroll to bottom (Signal-style: only if user is near bottom) ──
  const isNearBottom = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  // Where to land when the chat opens: the first unread message, or the end.
  // The conversation used to open wherever the browser happened to leave the
  // scroll — often mid-history — so the user had to hunt for their place.
  const didInitialScrollRef = useRef(false);
  useEffect(() => { didInitialScrollRef.current = false; }, [chatUser?.id]);

  useEffect(() => {
    if (didInitialScrollRef.current || chatMessages.length === 0) return;
    didInitialScrollRef.current = true;
    requestAnimationFrame(() => {
      const el = messagesRef.current;
      if (!el) return;
      const target = anchorMessageId
        ? el.querySelector(`[data-mid="${anchorMessageId}"]`)
        : null;
      if (target) {
        // Show the anchor with a little context above it, not glued to the edge.
        el.scrollTop = Math.max(0, target.offsetTop - 80);
      } else {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, [chatMessages, anchorMessageId, chatUser?.id]);

  // Growing the list from the top must not move what the user is reading:
  // remember the distance from the bottom and restore it after the prepend.
  const prependAnchorRef = useRef(null);
  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el || prependAnchorRef.current == null) return;
    el.scrollTop = el.scrollHeight - prependAnchorRef.current;
    prependAnchorRef.current = null;
  }, [chatMessages]);

  // Reaching the top pulls in the previous page.
  const handleScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el || !onLoadOlder || !hasMoreHistory) return;
    if (el.scrollTop < 120) {
      prependAnchorRef.current = el.scrollHeight - el.scrollTop;
      onLoadOlder();
    }
  }, [onLoadOlder, hasMoreHistory]);

  useEffect(() => {
    if (!didInitialScrollRef.current) return;
    if (isNearBottom()) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [chatMessages, bufferedMessages, isNearBottom]);

  // Scroll when keyboard opens (once, not on every resize tick)
  useEffect(() => {
    if (keyboardVisible) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [keyboardVisible]);

  // ── Anti-spam: reset on incoming message (active dialog) ──
  const prevMsgCountRef = useRef(chatMessages.length);
  useEffect(() => {
    if (chatMessages.length > prevMsgCountRef.current) {
      const newMsgs = chatMessages.slice(prevMsgCountRef.current);
      const hasIncoming = newMsgs.some(m => m.from_user_id !== userId && !m._pending);
      if (hasIncoming) {
        sentTimesRef.current = [];
      }
    }
    prevMsgCountRef.current = chatMessages.length;
  }, [chatMessages, userId]);

  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const onInput = (e) => {
    setChatInput(e.target.value);
    const ta = textareaRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'; }
  };

  useEffect(() => {
    return () => { if (composeIntervalRef.current) clearInterval(composeIntervalRef.current); };
  }, []);

  // ── Anti-spam countdown ──
  const startCountdown = useCallback(() => {
    composeDeadlineRef.current = Date.now() + COMPOSE_TIMEOUT_S * 1000;
    setComposeCountdown(COMPOSE_TIMEOUT_S);
    if (composeIntervalRef.current) clearInterval(composeIntervalRef.current);
    composeIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((composeDeadlineRef.current - Date.now()) / 1000));
      setComposeCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(composeIntervalRef.current);
        composeIntervalRef.current = null;
        if (!editingRef.current) {
          setBufferedMessages(prev => {
            if (prev.length > 0) sendMessage(prev.join('\n'));
            return [];
          });
          setComposeMode(false);
          setEditingBuffer(false);
        }
      }
    }, 500);
  }, [sendMessage]);

  const resetCountdown = useCallback(() => {
    composeDeadlineRef.current = Date.now() + COMPOSE_TIMEOUT_S * 1000;
    setComposeCountdown(COMPOSE_TIMEOUT_S);
  }, []);

  const checkSpam = useCallback(() => {
    const now = Date.now();
    const recent = sentTimesRef.current.filter(t => now - t < SPAM_WINDOW_MS);
    sentTimesRef.current = recent;
    return recent.length > SPAM_MSG_LIMIT;
  }, []);

  const flushBuffer = useCallback(() => {
    if (composeIntervalRef.current) clearInterval(composeIntervalRef.current);
    composeIntervalRef.current = null;
    setBufferedMessages(prev => {
      if (prev.length > 0) sendMessage(prev.join('\n'));
      return [];
    });
    setComposeMode(false);
    setEditingBuffer(false);
  }, [sendMessage]);

  const startEditingBuffer = () => {
    setEditBufferText(bufferedMessages.join('\n'));
    setEditingBuffer(true);
  };

  const finishEditingBuffer = () => {
    const text = editBufferText.trim();
    if (text) setBufferedMessages(text.split('\n').filter(l => l.trim()));
    setEditingBuffer(false);
    const remaining = Math.max(0, Math.ceil((composeDeadlineRef.current - Date.now()) / 1000));
    if (remaining <= 0) {
      const lines = text.trim().split('\n').filter(l => l.trim());
      if (lines.length > 0) sendMessage(lines.join('\n'));
      setBufferedMessages([]);
      setComposeMode(false);
      if (composeIntervalRef.current) clearInterval(composeIntervalRef.current);
    }
  };

  // ── Clear input without losing keyboard focus (iOS fix) ──
  const clearInput = useCallback(() => {
    const ta = textareaRef.current;
    if (ta) {
      // Reset height first
      ta.style.height = 'auto';
      // Set value via DOM to avoid React re-render focus loss
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
      nativeInputValueSetter.call(ta, '');
      ta.dispatchEvent(new Event('input', { bubbles: true }));
    }
    setChatInput('');
    // Refocus via rAF to keep keyboard on iOS
    requestAnimationFrame(() => { ta?.focus(); });
  }, [setChatInput]);

  // ── Send text message ──
  const handleSend = () => {
    const ta = textareaRef.current;
    const text = ta ? ta.value.trim() : chatInput.trim();
    if (!text) return;

    sentTimesRef.current.push(Date.now());

    if (composeMode) {
      setBufferedMessages(prev => [...prev, text]);
      clearInput();
      resetCountdown();
      return;
    }

    if (checkSpam()) {
      setComposeMode(true);
      setBufferedMessages([text]);
      clearInput();
      startCountdown();
      return;
    }

    // Normal send — include replyToId if set
    sendMessage(text, undefined, undefined, replyTo?.id || undefined);
    setReplyTo(null);
    clearInput();
  };

  const dismissKeyboard = () => { textareaRef.current?.blur(); };
  const fmtCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Reply: scroll to original message ──
  const scrollToMessage = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.background = 'rgba(91,141,239,0.15)';
      setTimeout(() => { el.style.background = ''; }, 1500);
    }
  };

  // ── Reply: set reply target ──
  const handleReply = (msg) => {
    setCtxMenu(null);
    setReplyTo(msg);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  // ── Context menu helpers ──
  const openCtxMenu = (msg, x, y) => {
    const cx = Math.min(x, window.innerWidth - 168);
    const cy = Math.max(8, y - 52);
    setCtxMenu({ msg, x: cx, y: cy });
  };
  const closeCtxMenu = () => setCtxMenu(null);

  // ── Voice recording ──
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      recordChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      mr.start();
      mediaRecorderRef.current = mr;
      recordStartRef.current = Date.now();
      recordingRef.current = true;
      setRecording(true);
      setRecordLocked(false);
      setSlideX(0);
      setLockProgress(0);
      setRecordDuration(0);
      setWaveformBars([]);

      // Timer
      recordTimerRef.current = setInterval(() => {
        setRecordDuration(Math.floor((Date.now() - recordStartRef.current) / 1000));
      }, 200);

      // Audio analyser for waveform
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);
        analyserRef.current = analyser;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        waveIntervalRef.current = setInterval(() => {
          if (!recordingRef.current) return;
          analyser.getByteFrequencyData(dataArray);
          // Take average of low-mid frequencies for a single bar value
          const slice = dataArray.slice(2, 30);
          const avg = slice.reduce((a, b) => a + b, 0) / slice.length / 255;
          setWaveformBars(prev => {
            const next = [...prev, Math.max(0.08, avg)];
            return next.length > 48 ? next.slice(-48) : next;
          });
        }, 80);
      } catch (audioErr) {
        console.warn('AudioContext for waveform failed:', audioErr);
      }

      if (navigator.vibrate) navigator.vibrate(15);
    } catch (err) { console.warn('Mic access denied:', err); }
  };

  const cleanupRecording = useCallback(() => {
    clearInterval(recordTimerRef.current);
    clearInterval(waveIntervalRef.current);
    recordingRef.current = false;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setRecording(false);
    setRecordLocked(false);
    recordLockedRef.current = false;
    setSlideX(0);
    setLockProgress(0);
    setWaveformBars([]);
  }, []);

  const stopRecording = useCallback(async (cancelled) => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') { cleanupRecording(); return; }
    return new Promise((resolve) => {
      mr.onstop = async () => {
        if (cancelled) { cleanupRecording(); resolve(); return; }
        const blob = new Blob(recordChunksRef.current, { type: 'audio/webm' });
        const duration = Math.round((Date.now() - recordStartRef.current) / 1000);
        if (duration < 1) { cleanupRecording(); resolve(); return; }
        try {
          const file = new File([blob], 'voice.webm', { type: 'audio/webm' });
          const { meta } = await api.uploadSealed(file, { duration });
          sendMessage(null, 'voice', meta);
        } catch (e) { console.error('Voice upload failed:', e); }
        cleanupRecording();
        resolve();
      };
      mr.stop();
    });
  }, [sendMessage, cleanupRecording]);

  const CANCEL_THRESHOLD = 100; // px to left for cancel
  const LOCK_THRESHOLD = 70;   // px up to lock

  const handleRecordGesture = useCallback((dx, dy) => {
    // dx = horizontal movement (negative = left), dy = vertical up (positive = up)
    if (dx < -CANCEL_THRESHOLD) {
      // Cancel — stop recording immediately
      stopRecording(true);
      if (navigator.vibrate) navigator.vibrate(10);
      return;
    }
    if (dy > LOCK_THRESHOLD) {
      // Lock
      setRecordLocked(true);
      recordLockedRef.current = true;
      setSlideX(0);
      setLockProgress(1);
      if (navigator.vibrate) navigator.vibrate([10, 30, 10]);
      return;
    }
    // Visual feedback
    setSlideX(Math.min(0, dx));
    setLockProgress(Math.max(0, Math.min(1, dy / LOCK_THRESHOLD)));
  }, [stopRecording]);

  // Touch & mouse — hold ≥150ms to start recording, tap is ignored
  const HOLD_DELAY = 150; // ms — distinguish tap from hold
  const onMicTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    const holdTimer = setTimeout(() => startRecording(), HOLD_DELAY);
    const onTouchMove = (ev) => {
      ev.preventDefault();
      if (!recordingRef.current || recordLockedRef.current) return;
      const t = ev.touches[0];
      const dx = t.clientX - touchStartRef.current.x;
      const dy = touchStartRef.current.y - t.clientY;
      handleRecordGesture(dx, dy);
    };
    const onTouchEnd = () => {
      clearTimeout(holdTimer);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
      if (!recordingRef.current) return; // was just a tap — ignore
      if (recordLockedRef.current) return;
      stopRecording(false);
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
  };
  const onMicMouseDown = (e) => {
    e.preventDefault();
    touchStartRef.current = { x: e.clientX, y: e.clientY };
    const holdTimer = setTimeout(() => startRecording(), HOLD_DELAY);
    const onMove = (ev) => {
      if (!touchStartRef.current || !recordingRef.current) return;
      if (recordLockedRef.current) return;
      const dx = ev.clientX - touchStartRef.current.x;
      const dy = touchStartRef.current.y - ev.clientY;
      handleRecordGesture(dx, dy);
    };
    const onUp = () => {
      clearTimeout(holdTimer);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!recordingRef.current) return; // was just a click — ignore
      if (recordLockedRef.current) return;
      stopRecording(false);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const fmtRecordTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Upload a single file (decides type automatically) ──
  const uploadAndSend = useCallback(async (file) => {
    const isImage = file.type.startsWith('image/');
    try {
      const { fileId, meta } = await api.uploadSealed(file);
      console.log('[Attach] uploaded sealed:', file.name, 'fileId:', fileId);
      sendMessage(null, isImage ? 'image' : 'file', meta);
    } catch (err) { console.error('[Attach] upload failed:', file.name, err); }
  }, [sendMessage]);

  // ── Attachments (single file from picker) ──
  const handleFileSelected = async (e, type) => {
    console.log('[Attach] handleFileSelected fired, type:', type);
    const files = Array.from(e.target.files || []);
    if (files.length === 0) { console.log('[Attach] no file selected (picker cancelled)'); return; }
    e.target.value = '';
    setShowAttachMenu(false);

    // Single image from camera/photo — show preview
    if ((type === 'image' || type === 'camera') && files.length === 1) {
      console.log('[Attach] image preview:', files[0].name, files[0].type, files[0].size);
      setImagePreview({ file: files[0], url: URL.createObjectURL(files[0]) });
      return;
    }

    // Multiple files or document — upload all directly
    for (const file of files) {
      console.log('[Attach] file:', file.name, file.type, file.size);
      await uploadAndSend(file);
    }
  };
  const sendImagePreview = async () => {
    if (!imagePreview || isUploading) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      // Compress image (unless HD mode or GIF)
      const opts = hdMode ? COMPRESS_HD : COMPRESS_DEFAULT;
      const compressed = await compressImage(imagePreview.file, opts);
      console.log('[Attach] compressed:', imagePreview.file.size, '→', compressed.size, 'bytes', hdMode ? '(HD)' : '');

      // Upload with progress tracking; the blob is sealed on this device
      // and the server stores ciphertext it cannot open (SEC-04).
      const named = new File([compressed], imagePreview.file.name || 'image.jpg', { type: compressed.type || 'image/jpeg' });
      const { fileId, meta } = await api.uploadSealed(named, { onProgress: setUploadProgress });

      // Cache the preview URL so ImageMessage doesn't re-download
      imageCache.set(fileId, imagePreview.url);

      sendMessage(null, 'image', meta);
      // Don't revoke URL — it's now in imageCache
      setImagePreview(null);
    } catch (err) {
      console.error('Image upload failed:', err);
      URL.revokeObjectURL(imagePreview.url);
      setImagePreview(null);
    }
    setIsUploading(false);
    setUploadProgress(0);
    setHdMode(false);
  };
  const cancelImagePreview = () => {
    if (isUploading) return; // Don't cancel during upload
    if (imagePreview) URL.revokeObjectURL(imagePreview.url);
    setImagePreview(null);
    setHdMode(false);
  };

  // ── Drag & Drop ──
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setDragOver(true);
    }
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragOver(false);
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    console.log('[Drop] files:', files.length, files.map(f => f.name));
    // Single image → preview, else upload all
    if (files.length === 1 && files[0].type.startsWith('image/')) {
      setImagePreview({ file: files[0], url: URL.createObjectURL(files[0]) });
    } else {
      for (const file of files) {
        await uploadAndSend(file);
      }
    }
  };

  const hasText = chatInput.trim().length > 0;

  // ── Reply preview text for the bar ──
  const replyPreview = replyTo ? (() => {
    const rt = replyTo.msgType || replyTo.type || 'text';
    if (rt === 'voice') return '🎤 Голосовое';
    if (rt === 'image') return '📷 Фото';
    if (rt === 'file') return '📎 ' + (replyTo.file_name || 'Файл');
    return (replyTo.content || '').slice(0, 60);
  })() : '';

  return (
    <div ref={overlayRef} style={vvBox.height != null ? { ...CS.overlay, top: vvBox.top, height: vvBox.height, bottom: 'auto' } : CS.overlay}
      onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}
    >
      {/* Drag & drop overlay */}
      {dragOver && (
        <div style={CS.dropZone}>
          <div style={CS.dropZoneInner}>
            <Upload size={48} color={colors.avatarBlue} />
            <div style={CS.dropZoneText}>Перетяните файлы сюда</div>
            <div style={CS.dropZoneHint}>Изображения, документы, аудио</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={CS.header}>
        <button onClick={onBack} style={CS.backBtn}><ArrowLeft size={22} color={colors.gray900} /></button>
        <div style={CS.headerAvatar}>{nameInitials(chatUser)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={CS.headerName}>{displayName(chatUser)}</div>
          <div style={CS.headerSub}>{formatLastSeen(chatUser.last_seen) || `ext. ${chatUser.sip_extension}`}</div>
        </div>
        <button onClick={onCall} style={CS.callBtn}><Phone size={20} color={colors.gray900} /></button>
      </div>

      {/* A device of this person whose key we have never seen. Sending stays
          blocked until the person says it is theirs: a new phone and an
          attacker look exactly the same from here, and only they can tell. */}
      {untrustedDevices.length > 0 && (
        <div style={{
          background: 'rgba(220,120,40,0.14)', borderBottom: `1px solid ${colors.gray300}`,
          padding: '12px 16px', fontFamily: FONT, fontSize: 13, color: colors.gray900,
        }}>
          {/* Two refusals wear the same banner and only one of them has a
              button that helps. A device nobody has confirmed is a decision
              for the person; a device the OTHER account never signed for is
              not — no amount of confirming here changes what their account
              said, and offering the button anyway had somebody pressing it
              three times in a row while nothing happened. */}
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {untrustedDevices.some(d => d.masterChanged)
              ? 'У собеседника новый ключ аккаунта'
              : untrustedDevices.some(d => d.unlisted)
                ? 'Устройство собеседника не входит в его список'
                : untrustedDevices.length === 1 ? 'Новое устройство у собеседника' : 'Новые устройства у собеседника'}
          </div>
          <div style={{ opacity: 0.85, marginBottom: 10 }}>
            {untrustedDevices.some(d => d.masterChanged)
              ? 'Отправка остановлена. Список его устройств подписан НЕ тем ключом аккаунта, '
                + 'который этот телефон запомнил. Так выглядит человек, потерявший все устройства '
                + 'и начавший заново, — и так же выглядит подмена со стороны сервера. '
                + 'Сверьте отпечаток голосом или другим каналом, и только потом принимайте новый ключ.'
              : untrustedDevices.some(d => d.unlisted)
              ? 'Отправка остановлена. Список устройств подписывает сам аккаунт собеседника, '
                + 'и этого устройства в нём нет. Подтвердить его отсюда нельзя — это может '
                + 'сделать только он, у себя, на том устройстве, где лежит ключ его аккаунта.'
              : 'Отправка остановлена. Так выглядит и новый телефон, и попытка вклиниться в '
                + 'переписку — отличить может только человек. Подтвердите, если это его устройство.'}
          </div>
          {untrustedDevices.some(d => d.masterChanged) && onAcceptNewAccountKey && (
            <button onClick={onAcceptNewAccountKey}
              style={{ border: 'none', borderRadius: radii.sm, padding: '8px 14px', marginBottom: 6,
                       fontFamily: FONT, fontSize: 13, cursor: 'pointer', background: colors.gray900, color: colors.white }}>
              Он начал аккаунт заново — принять новый ключ
            </button>
          )}
          {untrustedDevices.map(d => (
            <div key={d.deviceId} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {d.deviceLabel || 'устройство'} · {shortFingerprint(d.identitySig)}
              </span>
              {d.unlisted ? (
                <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.75 }}>
                  ждём, пока он подтвердит у себя
                </span>
              ) : (
              <button
                onClick={() => onTrustDevice && onTrustDevice(d)}
                style={{
                  marginLeft: 'auto', border: 'none', borderRadius: radii.sm,
                  padding: '6px 12px', fontFamily: FONT, fontSize: 13, cursor: 'pointer',
                  background: colors.gray900, color: colors.white,
                }}
              >Подтвердить</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={messagesRef} onScroll={handleScroll} style={{
        ...CS.messages,
        ...(composeMode ? CS.messagesComposeGlow : {}),
      }}>
        {chatMessages.length === 0 && bufferedMessages.length === 0 ? (
          <div style={CS.emptyState}>
            <MessageSquare size={44} color={colors.gray200} strokeWidth={1.5} />
            <div style={CS.emptyText}>Начните диалог</div>
          </div>
        ) : (
          <div style={{ flex: 1 }} />
        )}
        {chatMessages.map((msg, i) => {
          const isMine = msg.from_user_id === userId;
          const msgType = msg.msgType || msg.type || 'text';
          const isMedia = msgType === 'image';
          return (
            <SwipeableMessage key={msg.id || i} onReply={() => handleReply(msg)} onLongPress={(x, y) => openCtxMenu(msg, x, y)} direction={isMine ? 'left' : 'right'}>
              <div id={`msg-${msg.id}`} data-mid={msg.id} style={{ marginBottom: 16, transition: 'background 0.5s', borderRadius: 12, padding: '2px 0' }}>
                <div style={{
                  display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row',
                  gap: 10, alignItems: 'flex-start',
                }}>
                  <div style={isMine ? CS.myAvatar : CS.theirAvatar}>
                    {isMine ? (userName ? userName.charAt(0).toUpperCase() : 'U') : nameInitials(chatUser)}
                  </div>
                  <div style={{
                    ...CS.bubble,
                    ...(isMine ? CS.bubbleMine : CS.bubbleTheirs),
                    ...(isMedia ? { padding: 4, background: 'transparent' } : {}),
                  }}>
                    <ReplyQuote msg={msg} onTap={scrollToMessage} chatUserName={displayName(chatUser)} />
                    <MessageContent msg={msg} isMine={isMine} onOpenMedia={(m) => setMediaViewer(m)} />
                  </div>
                </div>
                <div style={{ ...CS.timestamp, display: 'flex', alignItems: 'center', justifyContent: isMine ? 'flex-end' : 'flex-start', paddingLeft: isMine ? 0 : 44, paddingRight: isMine ? 44 : 0 }}>
                  {fmtTime(msg.created_at)}
                  {isMine && <ReadReceipt read={msg.read} />}
                </div>
              </div>
            </SwipeableMessage>
          );
        })}

        {/* Buffered messages (anti-spam) */}
        {composeMode && bufferedMessages.length > 0 && (
          <div style={CS.bufferArea}>
            {editingBuffer ? (
              <div style={CS.bufferEditContainer}>
                <textarea value={editBufferText} onChange={(e) => setEditBufferText(e.target.value)} style={CS.bufferEditTextarea} autoFocus />
                <button onClick={finishEditingBuffer} style={CS.bufferDoneBtn}><Check size={18} color={colors.white} /></button>
              </div>
            ) : (
              <div onClick={startEditingBuffer} style={CS.bufferViewContainer}>
                {bufferedMessages.map((txt, i) => (
                  <div key={i} style={CS.bufferBubble}><div style={CS.bubbleText}>{txt}</div></div>
                ))}
                <div style={CS.bufferEditHint}><Edit3 size={12} color={colors.amber} /><span>Нажмите, чтобы редактировать</span></div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Image preview (before send) */}
      {imagePreview && (
        <div style={CS.previewOverlay} onClick={cancelImagePreview}>
          {/* Close button (top-right corner) */}
          {!isUploading && <button onClick={cancelImagePreview} style={CS.previewCloseBtn}><X size={24} color="#ece9e6" /></button>}
          <div style={CS.previewContainer} onClick={(e) => e.stopPropagation()}>
            <img src={imagePreview.url} alt="preview" style={CS.previewImg} />
            <div style={CS.previewActions}>
              {!isUploading && <button onClick={cancelImagePreview} style={CS.previewCancelBtn}><X size={20} color="#ece9e6" /></button>}
              {/* HD toggle */}
              {!isUploading && (
                <button onClick={() => setHdMode(!hdMode)} style={{
                  ...CS.previewHdBtn,
                  background: hdMode ? colors.avatarBlue : 'rgba(255,255,255,0.15)',
                  color: colors.white,
                }}>
                  HD
                </button>
              )}
              {/* Send / Progress */}
              {isUploading ? (
                <div style={CS.previewProgressWrap}>
                  <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3" />
                    <circle cx="22" cy="22" r="18" fill="none" stroke={colors.avatarBlue} strokeWidth="3"
                      strokeDasharray={`${2 * Math.PI * 18}`}
                      strokeDashoffset={`${2 * Math.PI * 18 * (1 - uploadProgress / 100)}`}
                      strokeLinecap="round" />
                  </svg>
                  <span style={CS.previewProgressText}>{uploadProgress}%</span>
                </div>
              ) : (
                <button onClick={sendImagePreview} style={CS.previewSendBtn}><Send size={20} color={colors.white} /></button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compose mode countdown banner */}
      {composeMode && (
        <div style={CS.composeBanner}>
          <span style={CS.composeBannerText}>Будет отправлено через {fmtCountdown(composeCountdown)}</span>
        </div>
      )}

      {/* Reply bar (above input) */}
      {replyTo && !recording && (
        <div style={CS.replyBar}>
          <div style={CS.replyBarLine} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={CS.replyBarName}>
              {replyTo.from_user_id === userId ? 'Вы' : displayName(chatUser)}
            </div>
            <div style={CS.replyBarText}>{replyPreview}</div>
          </div>
          <button onClick={() => setReplyTo(null)} style={CS.replyBarClose}>
            <X size={18} color={colors.gray400} />
          </button>
        </div>
      )}

      {/* Input Bar */}
      <div style={{
        ...CS.inputBar,
        bottom: 0,
        paddingBottom: keyboardVisible ? 8 : 'max(12px, env(safe-area-inset-bottom))',
        ...(recording && !recordLocked ? { overflow: 'visible' } : {}),
      }}>
        {/* Lock icon — floats ABOVE input bar during held recording */}
        {recording && !recordLocked && (
          <div style={{
            ...CS.lockIconWrap,
            transform: `translateY(${-lockProgress * 30}px) scale(${0.8 + lockProgress * 0.4})`,
            opacity: lockProgress > 0.05 ? 1 : 0.4,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={lockProgress > 0.7 ? colors.avatarBlue : colors.gray500} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d={lockProgress > 0.7 ? "M7 11V7a5 5 0 0 1 10 0v4" : "M7 11V7a5 5 0 0 1 9.9-1"}/>
            </svg>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={colors.gray400} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: -2 }}>
              <polyline points="18 15 12 9 6 15"/>
            </svg>
          </div>
        )}

        {/* Content area — changes based on state */}
        {recording ? (
          recordLocked ? (
            /* ── Locked recording UI ── */
            <div style={CS.lockedBar}>
              <button onClick={() => stopRecording(true)} style={CS.trashBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
              <div style={CS.lockedWaveArea}>
                <div style={CS.recordDot} />
                <span style={CS.recordTime}>{fmtRecordTime(recordDuration)}</span>
                <div style={CS.waveformContainer}>
                  {waveformBars.map((v, i) => (
                    <div key={i} style={{ ...CS.waveBar, height: Math.max(3, v * 24) }} />
                  ))}
                </div>
              </div>
              <button onClick={() => stopRecording(false)} style={CS.lockedSendBtn}>
                <Send size={20} color={colors.white} />
              </button>
            </div>
          ) : (
            /* ── Sliding recording UI (finger held) — no wrapper, direct flex children ── */
            <div style={{ ...CS.recordSlider, transform: `translateX(${slideX}px)` }}>
              <div style={CS.recordDot} />
              <span style={CS.recordTime}>{fmtRecordTime(recordDuration)}</span>
              <div style={CS.waveformContainer}>
                {waveformBars.slice(-20).map((v, i) => (
                  <div key={i} style={{ ...CS.waveBar, height: Math.max(3, v * 20) }} />
                ))}
              </div>
              <div style={{ ...CS.slideCancelHint, opacity: Math.min(1, Math.abs(slideX) / 60) }}>
                <span style={CS.slideCancelArrow}>‹</span>
                <span>Отмена</span>
              </div>
            </div>
          )
        ) : (
          <>
            <div style={{ position: 'relative', zIndex: showAttachMenu ? 71 : 'auto' }}>
              <button onClick={() => setShowAttachMenu(!showAttachMenu)} style={CS.attachBtn}><Paperclip size={20} color={colors.gray500} /></button>
              {showAttachMenu && (
                <div style={CS.attachMenu} onClick={(e) => e.stopPropagation()}>
                  <div style={{ ...CS.attachMenuItem, position: 'relative', overflow: 'hidden' }}>
                    <Camera size={18} color={colors.gray700} style={{ pointerEvents: 'none' }} /><span style={{ pointerEvents: 'none' }}>Камера</span>
                    <input type="file" accept="image/*" capture="environment" style={CS.overlayFileInput} onChange={(e) => handleFileSelected(e, 'camera')} />
                  </div>
                  <div style={{ ...CS.attachMenuItem, position: 'relative', overflow: 'hidden' }}>
                    <Image size={18} color={colors.gray700} style={{ pointerEvents: 'none' }} /><span style={{ pointerEvents: 'none' }}>Фото</span>
                    <input type="file" accept="image/*" multiple style={CS.overlayFileInput} onChange={(e) => handleFileSelected(e, 'image')} />
                  </div>
                  <div style={{ ...CS.attachMenuItem, position: 'relative', overflow: 'hidden' }}>
                    <FileText size={18} color={colors.gray700} style={{ pointerEvents: 'none' }} /><span style={{ pointerEvents: 'none' }}>Документ</span>
                    <input type="file" accept="*/*" multiple style={CS.overlayFileInput} onChange={(e) => handleFileSelected(e, 'document')} />
                  </div>
                </div>
              )}
            </div>
            <textarea ref={textareaRef} style={CS.textarea} value={chatInput} onChange={onInput}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(); } }}
              placeholder="Сообщение..." rows={1} enterKeyHint="send" autoComplete="off" />
            {keyboardVisible && (
              <button onClick={dismissKeyboard} style={CS.dismissKbBtn}><ChevronDown size={20} color={colors.gray400} /></button>
            )}
          </>
        )}

        {/* Right action button — always at the same DOM position */}
        {recording && recordLocked ? null : hasText && !recording ? (
          <button onClick={handleSend} style={{ ...CS.sendBtn, opacity: 1 }}><Send size={18} color={colors.white} /></button>
        ) : (
          <button
            onTouchStart={onMicTouchStart}
            onMouseDown={onMicMouseDown}
            style={recording ? CS.recordingMicBtn : CS.micBtn}
          >
            <Mic size={recording ? 22 : 20} color={recording ? colors.white : colors.gray500} />
          </button>
        )}
      </div>

      {/* Media viewer overlay */}
      {mediaViewer && (
        <MediaViewer
          media={mediaViewer}
          onClose={() => setMediaViewer(null)}
          onEdit={(fileId, blobUrl) => setImageEditor({ fileId, blobUrl })}
        />
      )}

      {/* Image editor overlay */}
      {imageEditor && (
        <ImageEditor
          imageUrl={imageEditor.blobUrl}
          onCancel={() => setImageEditor(null)}
          onSave={async (blob) => {
            try {
              const file = new File([blob], 'edited.jpg', { type: 'image/jpeg' });
              const { fileId, meta } = await api.uploadSealed(file);
              imageCache.set(fileId, URL.createObjectURL(blob));
              sendMessage(null, 'image', meta);
              setImageEditor(null);
              setMediaViewer(null);
            } catch (err) {
              console.error('[ImageEditor] upload failed:', err);
            }
          }}
        />
      )}

      {showAttachMenu && <div onClick={() => setShowAttachMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 69 }} />}

      {/* Long-press context menu */}
      {ctxMenu && (
        <>
          <div onClick={closeCtxMenu} style={{ position: 'fixed', inset: 0, zIndex: 80 }} />
          <div style={{
            position: 'fixed', zIndex: 81, left: ctxMenu.x, top: ctxMenu.y,
            background: colors.white, borderRadius: 12, padding: 4,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)', minWidth: 150,
          }}>
            <button onClick={() => handleReply(ctxMenu.msg)} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', width: '100%',
              background: 'none', border: 'none', borderRadius: 8,
              cursor: 'pointer', fontSize: 14, fontFamily: FONT,
              color: colors.gray700,
            }}>
              <CornerUpLeft size={16} color={colors.gray700} />
              <span>Ответить</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────
const chatStyles = {
  overlay: {
    // Longhands on purpose: React drops a shorthand (`inset`) when a longhand
    // it overlaps (`top`) is later added and removed, and the overlay fell
    // to its static position with the keyboard up (Pixel, 2026-09-02).
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60,
    background: colors.white,
    display: 'flex', flexDirection: 'column',
    fontFamily: FONT,
    animation: 'chatSlideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    overscrollBehavior: 'none',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12,
    paddingTop: 'max(12px, env(safe-area-inset-top))',
    paddingLeft: 4, paddingRight: 16, paddingBottom: 12,
    background: colors.white,
    borderBottom: `1px solid ${colors.gray200}`,
    flexShrink: 0,
  },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' },
  headerAvatar: { width: 40, height: 40, borderRadius: '50%', background: colors.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: colors.gray900, flexShrink: 0 },
  headerName: { fontSize: 17, fontWeight: 600, color: colors.gray900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  headerSub: { fontSize: 12, color: colors.gray400, marginTop: 1 },
  callBtn: { background: colors.gray50, border: `1px solid ${colors.gray200}`, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
  messages: {
    flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    padding: '20px 16px 8px',
    display: 'flex', flexDirection: 'column',
    transition: 'box-shadow 0.3s, border-color 0.3s',
  },
  messagesComposeGlow: {
    boxShadow: 'inset 0 0 12px 3px rgba(245,158,11,0.25)',
    borderLeft: '2px solid rgba(245,158,11,0.5)',
    borderRight: '2px solid rgba(245,158,11,0.5)',
    animation: 'composeGlow 2s ease-in-out infinite',
  },
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 },
  emptyText: { fontSize: 15, fontWeight: 500, color: colors.gray300 },
  theirAvatar: { width: 34, height: 34, borderRadius: '50%', background: colors.gray200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: colors.gray600, flexShrink: 0 },
  myAvatar: { width: 34, height: 34, borderRadius: '50%', background: colors.avatarBlue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: colors.white, flexShrink: 0 },
  bubble: { maxWidth: '75%', padding: '12px 16px', borderRadius: radii.lg, fontSize: 15, lineHeight: 1.5 },
  bubbleMine: { background: colors.bubbleBlue, color: colors.gray900 },
  bubbleTheirs: { background: colors.gray100, color: colors.gray900 },
  bubbleText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  timestamp: { fontSize: 12, color: colors.gray400, marginTop: 6 },

  // ── Reply quote (inside bubble) ──
  replyQuote: {
    display: 'flex', gap: 8, alignItems: 'stretch',
    padding: '6px 0', marginBottom: 6,
    cursor: 'pointer', borderRadius: 6,
  },
  replyQuoteBar: {
    width: 3, borderRadius: 2,
    background: colors.avatarBlue, flexShrink: 0,
  },
  replyQuoteName: {
    fontSize: 12, fontWeight: 600, color: colors.avatarBlue,
    fontFamily: FONT, lineHeight: 1.2,
  },
  replyQuoteText: {
    fontSize: 13, color: colors.gray500,
    fontFamily: FONT, lineHeight: 1.3,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: 200,
  },

  // ── Reply bar (above input) ──
  replyBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 16px',
    background: colors.gray50,
    borderTop: `1px solid ${colors.gray200}`,
    flexShrink: 0,
  },
  replyBarLine: {
    width: 3, height: 32, borderRadius: 2,
    background: colors.avatarBlue, flexShrink: 0,
  },
  replyBarName: {
    fontSize: 12, fontWeight: 600, color: colors.avatarBlue,
    fontFamily: FONT,
  },
  replyBarText: {
    fontSize: 13, color: colors.gray500, fontFamily: FONT,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  replyBarClose: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },

  // ── Buffer area ──
  bufferArea: { marginTop: 8, padding: 10, borderRadius: 14, border: '1.5px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.06)', position: 'relative' },
  bufferViewContainer: { cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 },
  bufferBubble: { background: colors.bubbleBlue, color: colors.gray900, padding: '10px 14px', borderRadius: 12, fontSize: 15, lineHeight: 1.5, alignSelf: 'flex-end', maxWidth: '85%' },
  bufferEditHint: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: colors.amber, fontFamily: FONT, alignSelf: 'center', marginTop: 4, opacity: 0.8 },
  bufferEditContainer: { display: 'flex', flexDirection: 'column', gap: 8 },
  bufferEditTextarea: { width: '100%', minHeight: 80, background: colors.white, border: `1.5px solid ${colors.amber}`, borderRadius: 12, padding: '10px 14px', fontSize: 15, fontFamily: FONT, color: colors.gray900, outline: 'none', resize: 'vertical', lineHeight: 1.5 },
  bufferDoneBtn: { alignSelf: 'flex-end', width: 36, height: 36, borderRadius: '50%', background: colors.amber, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },

  // ── Compose banner ──
  composeBanner: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 16px', background: 'rgba(245,158,11,0.12)', borderTop: '1px solid rgba(245,158,11,0.3)', flexShrink: 0 },
  composeBannerText: { fontSize: 13, fontFamily: FONT, color: colors.amber, fontWeight: 500, fontVariantNumeric: 'tabular-nums' },

  // ── Input bar ──
  inputBar: { position: 'relative', display: 'flex', gap: 8, alignItems: 'flex-end', padding: '12px 12px', paddingBottom: 'max(14px, env(safe-area-inset-bottom))', background: colors.white, borderTop: `1px solid ${colors.gray200}`, flexShrink: 0, flexWrap: 'wrap' },
  textarea: { flex: 1, background: colors.gray100, border: 'none', borderRadius: 24, padding: '12px 20px', fontSize: 16, fontFamily: FONT, color: colors.gray900, outline: 'none', resize: 'none', lineHeight: 1.4, maxHeight: 120, overflowY: 'auto' },
  sendBtn: { width: 44, height: 44, borderRadius: '50%', background: colors.gray900, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'opacity 0.15s' },
  micBtn: { width: 44, height: 44, borderRadius: '50%', background: colors.gray100, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none' },
  dismissKbBtn: { width: 36, height: 36, borderRadius: '50%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0, marginBottom: 4 },

  // ── Attach ──
  attachBtn: { width: 44, height: 44, borderRadius: '50%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
  attachMenu: { position: 'absolute', bottom: 52, left: 0, zIndex: 70, background: colors.white, borderRadius: 14, padding: 6, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160 },
  attachMenuItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: FONT, color: colors.gray700, whiteSpace: 'nowrap', WebkitTapHighlightColor: 'rgba(0,0,0,0.05)' },
  hiddenFileInput: { position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', left: 0, top: 0 },
  overlayFileInput: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0.01, cursor: 'pointer', zIndex: 10, margin: 0, padding: 0, border: 'none', fontSize: 100 },

  // ── Recording (sliding mode — finger held) ──
  recordSlider: {
    display: 'flex', alignItems: 'center', gap: 10,
    flex: 1, transition: 'none', paddingLeft: 4, minWidth: 0,
  },
  recordDot: {
    width: 8, height: 8, borderRadius: '50%',
    background: colors.red, flexShrink: 0,
    animation: 'recordPulse 1.2s ease-in-out infinite',
  },
  recordTime: {
    fontSize: 15, fontWeight: 600, color: colors.gray900,
    fontFamily: FONT, fontVariantNumeric: 'tabular-nums',
    minWidth: 36, flexShrink: 0,
  },
  slideCancelHint: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: 13, color: colors.gray400, fontFamily: FONT,
    transition: 'opacity 0.15s', flexShrink: 0,
    marginLeft: 'auto', paddingRight: 8,
  },
  slideCancelArrow: {
    fontSize: 18, lineHeight: 1, fontWeight: 300,
    animation: 'slideArrowPulse 1s ease-in-out infinite',
  },
  lockIconWrap: {
    position: 'absolute', right: 18, bottom: 'calc(100% + 8px)',
    width: 36, height: 52, borderRadius: 18,
    background: colors.white, border: `1px solid ${colors.gray200}`,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.1s, opacity 0.15s',
    zIndex: 10, gap: 0,
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  },
  recordingMicBtn: {
    width: 44, height: 44, borderRadius: '50%',
    background: colors.red, border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, zIndex: 1, cursor: 'pointer',
    animation: 'micPulse 1.5s ease-in-out infinite',
    boxShadow: '0 0 0 4px rgba(221,122,92,0.2)',
    touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
  },
  // ── Recording (locked mode — finger released) ──
  lockedBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    flex: 1, height: 44,
    animation: 'lockedBarIn 0.2s ease-out',
  },
  trashBtn: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'rgba(221,122,92,0.1)', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  lockedWaveArea: {
    flex: 1, display: 'flex', alignItems: 'center', gap: 8,
    minWidth: 0, overflow: 'hidden',
  },
  waveformContainer: {
    display: 'flex', alignItems: 'center', gap: 2,
    flex: 1, minWidth: 0, height: 24, overflow: 'hidden',
  },
  waveBar: {
    width: 3, borderRadius: 1.5,
    background: colors.avatarBlue, flexShrink: 0,
    transition: 'height 0.1s ease-out',
  },
  lockedSendBtn: {
    width: 44, height: 44, borderRadius: '50%',
    background: colors.avatarBlue, border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },

  // ── Drag & drop zone ──
  dropZone: { position: 'fixed', inset: 0, zIndex: 250, background: 'rgba(21,20,26,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' },
  dropZoneInner: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, borderRadius: 24, border: `3px dashed ${colors.avatarBlue}`, background: 'rgba(91,141,239,0.06)' },
  dropZoneText: { fontSize: 18, fontWeight: 600, color: colors.gray900, fontFamily: FONT },
  dropZoneHint: { fontSize: 13, color: colors.gray400, fontFamily: FONT },

  // ── Image preview (before send) ──
  previewOverlay: { position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  previewCloseBtn: { position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', right: 16, zIndex: 201, width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  previewContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, maxWidth: '90vw', maxHeight: '80vh', cursor: 'default' },
  previewImg: { maxWidth: '90vw', maxHeight: '60vh', objectFit: 'contain', borderRadius: 12 },
  previewActions: { display: 'flex', gap: 16, alignItems: 'center', paddingBottom: 'max(16px, env(safe-area-inset-bottom))' },
  previewCancelBtn: { width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  previewSendBtn: { width: 56, height: 56, borderRadius: '50%', background: '#6e8ad0', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  previewHdBtn: { height: 36, padding: '0 14px', borderRadius: 18, border: 'none', fontWeight: 700, fontSize: 14, letterSpacing: 1, cursor: 'pointer', WebkitTapHighlightColor: 'transparent', transition: 'background 0.2s' },
  previewProgressWrap: { width: 56, height: 56, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  previewProgressText: { position: 'absolute', color: '#ece9e6', fontSize: 11, fontWeight: 700 },
};

// Copy to module-level CS for use in sub-components
Object.assign(CS, chatStyles);
